import { HttpErrorResponse } from '@angular/common/http';
import { SapError } from 'src/app/lego-utils';
import { GraphError } from '@microsoft/microsoft-graph-client';

// Structural, not `Store` from Akita, so this accepts both the remaining Akita
// stores and the step-4c `CollectionState` replacements while domains migrate.
// The helper only ever passes a message or `undefined`.
interface IErrorSink {
  setError(error: string | undefined): void;
}

interface ICallbackPack {
  /**
   * Callback function to handle specific HTTP status codes
   *
   * Override the built-in handler for `httpErrorStatusCode`, if it doesn't have a handler then create it
   *
   * @example
   * {
   *   404: () => {
   *     // don't handle NOT FOUND as an error
   *     this.store.set([]);
   *     this.store.setError(undefined);
   *   }
   * }
   */
  [httpErrorStatusCode: number]: () => void;

  /**
   * Callback function to handle HTTP status codes that are not explicitly specified,
   * either via the built-in handlers or handled via ICallback
   *
   * Override the built-in default handler
   *
   * @example
   * {
   *   default: () => {
   *     this.store.setError(error.message + '🍆');
   *   }
   * }
   */
  default?(): void;

  /**
   * Callback function to handle an unknown error
   *
   * Override the built-in unknown handler
   *
   * @example
   * {
   *   unknown: () => {
   *     this.store.setError('Something unexpected happened! 🧨');
   *   }
   * }
   */
  unknown?(): void;
}

function handler<TStore extends IErrorSink>(
  store: TStore,
  handlers?: ICallbackPack,
  httpStatusCode?: number,
  errorMsg?: string
) {
  if (handlers && httpStatusCode) {
    const customHandlerFn = handlers[httpStatusCode];

    if (customHandlerFn instanceof Function) {
      customHandlerFn();

      return;
    }
  }

  switch (httpStatusCode) {
    case 404:
      store.setError(undefined);
      break;

    default:
      if (handlers && !!handlers.default) {
        handlers.default();
      } else {
        store.setError(errorMsg);
      }
  }
}

/**
 * Extracts the error message from the error object and sets it on the store
 *
 * @param store The store errors should be assigned to
 * @param error The error object
 * @param handlers Option bag that adds or overrides handlers
 */
export function handleError<TStore extends IErrorSink>(
  store: TStore,
  error: unknown,
  handlers?: ICallbackPack
) {
  if (error instanceof SapError) {
    handler(store, handlers, error.httpStatusCode, error.message);
  } else if (error instanceof GraphError) {
    const message = error.message
      ? `${error.message}${error.code ? ` (code: ${error.code})` : ''}`
      : 'No reason was given 😭';

    handler(store, handlers, error.statusCode, message);
  } else if (error instanceof HttpErrorResponse) {
    handler(store, handlers, error.status, error.message);
  } else {
    // handle unknown errors - this will most likely be a JS error, like calling undefined.foo()

    if (handlers && !!handlers.unknown) {
      handlers.unknown();
    } else {
      store.setError(String(error));
    }

    // print the stack trace to to console so we can find the file:line where it was thrown
    console.error(String(error));
  }
}
